import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { RevealButton } from '@/components/audit/RevealButton';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AdminRoleCard } from '@/features/users/AdminRoleCard';
import { revealUser } from '@/api/admin/users';
import { useAuditEvents } from '@/queries/audit';
import { useUserSummary } from '@/queries/users';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@/queries/queryKeys';
import type { AuditEvent } from '@/schemas/admin/audit';
import type { UserReveal } from '@/schemas/admin/users';

export const Route = createFileRoute('/_authed/users/$id')({
  component: UserDetailScreen,
});

function UserDetailScreen() {
  const { id } = Route.useParams();
  const { t } = useTranslation('users');
  const audit = useAuditEvents();
  const summary = useUserSummary(id);
  const qc = useQueryClient();

  const userAudit = React.useMemo(() => {
    if (!audit.data) return [];
    return audit.data.filter((e) => e.subject_kind === 'user' && e.subject_id === id);
  }, [audit.data, id]);

  const auditColumns: readonly DataColumn<AuditEvent>[] = [
    {
      id: 'when',
      header: t('audit.when'),
      cell: (r) => <DateTime value={r.created_at} />,
      sortBy: (r) => new Date(r.created_at),
    },
    {
      id: 'action',
      header: t('audit.action'),
      cell: (r) => (
        <Badge tone={r.action.startsWith('admin.pii_reveal') ? 'warn' : 'accent'}>{r.action}</Badge>
      ),
    },
    {
      id: 'actor',
      header: t('audit.actor'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.actor_id ? `${r.actor_id.slice(0, 8)}…` : '—'}
        </span>
      ),
    },
    {
      id: 'reason',
      header: t('audit.reason'),
      cell: (r) => <span className="text-xs">{r.reason ?? '—'}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <Link
        to="/users"
        className="inline-flex items-center gap-1 text-sm text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
      >
        <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden />
        {t('backToList')}
      </Link>

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('detailTitle')}</h1>
        {summary.data ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--color-fg-muted)]">
            <span className="font-medium text-[color:var(--color-fg)]" dir="ltr">
              @{summary.data.handle}
            </span>
            <Badge tone={summary.data.state === 'active' ? 'success' : 'warn'}>
              {summary.data.state}
            </Badge>
          </p>
        ) : null}
        <p className="font-mono text-sm text-[color:var(--color-fg-muted)]" dir="ltr">
          {id}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('admin.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.isLoading ? (
            <p className="text-sm text-[color:var(--color-fg-muted)]">{t('admin.loading')}</p>
          ) : summary.data ? (
            <AdminRoleCard summary={summary.data} />
          ) : (
            <p className="text-sm text-[color:var(--color-danger)]">{t('admin.loadError')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('contact.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[color:var(--color-fg-muted)]">{t('contact.body')}</p>
          <RevealButton<UserReveal>
            resourceId={id}
            fetch={() => revealUser(id)}
            onRevealed={() => {
              void qc.invalidateQueries({ queryKey: qk.audit() });
            }}
            render={(value) => (
              <span className="space-x-3 rtl:space-x-reverse">
                <span>{value.email}</span>
                <span className="text-[color:var(--color-fg-muted)]">@{value.handle}</span>
              </span>
            )}
            confirmDescription={t('contact.confirm')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">{t('audit.title')}</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/audit" search={{ subjectKind: 'user', subjectId: id }}>
              {t('audit.viewAll')}
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <DataTable<AuditEvent>
            data={userAudit}
            columns={auditColumns}
            rowKey={(r) => String(r.id)}
            loading={audit.isLoading}
            empty={{
              title: t('audit.emptyTitle'),
              body: t('audit.emptyBody'),
            }}
            initialSort={{ id: 'when', direction: 'desc' }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
